// =============================================================================
// TrialBanner.js, Calm in-app reminder for users currently on a trial.
//
// Rendered at the top of the app shell when isOnTrial(profile) is true.
// Shows the days remaining and a single, dignified CTA to "Add a payment
// method", which in Phase 32 routes to the placeholder /settings/billing
// page (real checkout wires up in Step G).
//
// Phase 49, Beta users see a fully different, payment-free variant:
// just a quiet welcome line + a Dismiss button. We never surface
// "Add payment method" to beta testers (no card, no charging during beta).
//
// Design notes:
//   • Quiet ambient style, not a red bug-report banner. We use the brand
//     gold accent because the trial IS a premium experience, not a warning.
//   • Auto-hides on day 0 (trial ended) so the dashboard returns to calm.
//   • Dismissable per-session via sessionStorage; we never permanently hide
//     because the trial countdown matters.
// =============================================================================
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { isOnTrial, trialDaysLeft, planMeta } from '@/lib/entitlements';

const SS_KEY = 'twentyOneTwentyTrialBannerDismissed';
const SS_BETA_KEY = 'twentyOneTwentyBetaBannerDismissed';

export default function TrialBanner() {
  const { profile } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // Phase 49, beta users get the beta variant (no payment CTA, payment-free copy).
  const isBeta = !!profile?.is_beta;
  const ssKey = isBeta ? SS_BETA_KEY : SS_KEY;

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(ssKey) === '1');
    } catch (e) {
      // sessionStorage can be disabled in private windows; treat as not-dismissed.
    }
  }, [ssKey]);

  if (!profile || dismissed) return null;
  // Beta users may not satisfy the trial gate (different flow) but still
  // benefit from the welcome banner. Non-beta requires an active trial.
  if (!isBeta && !isOnTrial(profile)) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(ssKey, '1'); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  // -------- Beta variant, no payment CTA, just a calm welcome line --------
  if (isBeta) {
    const betaPlanLabel = (profile.beta_plan || 'Horizon').toString();
    const betaPlanTitle = betaPlanLabel.charAt(0).toUpperCase() + betaPlanLabel.slice(1);
    return (
      <div
        data-testid="trial-banner"
        className="w-full border-b border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/8"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
          <Sparkles className="h-4 w-4 text-[hsl(var(--gold))] shrink-0" />
          <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium text-foreground" data-testid="trial-banner-headline">
              Welcome to the 2120 private beta.
            </span>
            <span className="text-xs text-muted-foreground truncate">
              You have full {betaPlanTitle} access while we shape this together. No card, no charges, your feedback is what we care about.
            </span>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="trial-banner-dismiss"
            aria-label="Dismiss"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // -------- Regular trial variant (unchanged) --------
  const days = trialDaysLeft(profile);
  const trialPlan = profile.trial_plan || 'horizon';
  const meta = planMeta(trialPlan);

  // Tone shift in the final 72 hours, same calm style, slightly warmer copy.
  const ending = days <= 3;
  const headline = ending
    ? `Your ${meta.label} trial ends in ${days} day${days === 1 ? '' : 's'}`
    : `${days} days left on your ${meta.label} trial`;
  const sub = ending
    ? 'Add a payment method to keep your full access. No charge until you confirm.'
    : 'Enjoying the wider view? Add a payment method anytime, we won’t charge you until your trial ends.';

  return (
    <div
      data-testid="trial-banner"
      className="w-full border-b border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/8"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <Sparkles className="h-4 w-4 text-[hsl(var(--gold))] shrink-0" />
        <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-foreground" data-testid="trial-banner-headline">
            {headline}
          </span>
          <span className="text-xs text-muted-foreground truncate">{sub}</span>
        </div>
        <Link
          to="/settings/billing"
          data-testid="trial-banner-cta"
          className="text-xs font-medium text-primary hover:underline whitespace-nowrap"
        >
          Add payment method
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="trial-banner-dismiss"
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
